import React from "react";
import { X } from "lucide-react";
import UpdateButton from "../../admin/components/alumnis/UpdateButton";
import DeleteButton from "../../admin/components/alumnis/DeleteButton";
import { Alumni } from "../../types/types";
import { motion, AnimatePresence } from "motion/react";
import {
  detailModalBackdropVariants,
  detailModalContentVariants,
} from "../../animations/animation";

interface DetailModalProps {
  alumni: Alumni;
  onClose: () => void;
  onUpdateClick: () => void;
  isOpen?: boolean;
}

const AlumniTableDetailModal: React.FC<DetailModalProps> = ({
  alumni,
  onClose,
  onUpdateClick,
  isOpen = true,
}) => {
  const formatDate = (date: string | null) => {
    if (!date) return "—";
    const d = new Date(date);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isVerified = (date: string | null) => (date ? "Yes" : "No");
  const isBanned = (date: string | null) => (date ? "Yes" : "No");

  const handleDeleteSuccess = () => {
    onClose();
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleBackdropClick}
          variants={detailModalBackdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8"
            variants={detailModalContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Alumni Details
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Profile Header */}
            <div className="flex items-start gap-6 mb-6 border-b pb-6">
              <div className="flex-shrink-0">
                <img
                  src={alumni.photo_url}
                  alt={alumni.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                />
              </div>
              <div className="flex-1 space-y-2">
                <h3 className="text-xl font-semibold text-gray-900">
                  {alumni.name}
                </h3>
                <p className="text-gray-600">{alumni.email}</p>
              </div>
            </div>

            {/* Two-column layout for details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Personal Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
                  Personal Information
                </h4>
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Gender:</span>
                    <span>{alumni.gender === "M" ? "Male" : "Female"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Phone:</span>
                    <span>{alumni.phone || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Birthday:</span>
                    <span>{new Date(alumni.dob).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Bio:</span>
                    <span className="text-gray-600">{alumni.bio ?? "—"}</span>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
                  Professional Information
                </h4>
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Role:</span>
                    <span>{alumni.role === "admin" ? "Admin" : "Alumni"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Company:</span>
                    <span>{alumni.company || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">
                      Email Verified:
                    </span>
                    <span>{isVerified(alumni.email_verified_at)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Joined:</span>
                    <span>{formatDate(alumni.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pt-4 border-t">
              <UpdateButton
                id={alumni.id}
                email={alumni.email}
                onClick={onUpdateClick}
              />
              <DeleteButton
                id={alumni.id}
                email={alumni.email}
                onDeleteSuccess={handleDeleteSuccess}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AlumniTableDetailModal;
